// =============================================================================
// ResiGen — dealer/sales-affiliate workspace: residential PV + ESS prepayment projects.
// Intake captures four inputs; the org's valuation tables (or Luminia defaults) resolve the
// PREPAYMENT AMOUNT the dealer copies into their own proposal software. No enrichments.
// =============================================================================

import { Meteor } from 'meteor/meteor';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { Button } from '/imports/ui/Button';
import { Input } from '/imports/ui/Input';
import { Modal, ModalStyled } from '/imports/ui/Modal';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { ResigenTablesModal } from '/imports/ui/ResigenTablesModal';
import { toastOnError } from '/imports/ui/utils';
import { useUser, useFindOne, useSubscribe, useTracker } from '/imports/hooks';
import { Publications } from '/imports/publications';
import { Accounts, Roles } from '/imports/api/accounts';
import { formatCurrency, formatNumber } from '/imports/utils';
import {
  ResigenProjects, ResigenProject, ResigenTables,
  resigenSaveProject, resigenGetTables, resigenRoofEstimate, resigenCalculate, resigenItcStatusLabels,
} from '/imports/api/resigenProjects';
import { AddressInput } from '/imports/ui/AddressInput';

export const resigenMoney = (n?: number) => (n == null ? '—' : formatCurrency(n, 0));

const ResiGenPageStyled = styled(Page)`
  .heading-buttons { display: flex; gap: 0.5rem; align-items: center; }
  .controls { display: flex; gap: 1rem; align-items: center; margin-bottom: 0.75rem; }
  .controls .search { min-width: 16rem; max-width: 28rem; flex: 1; }
  table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
  th, td { text-align: left; padding: 0.45rem 0.6rem; border-bottom: 1px solid ${css.grey6}; white-space: nowrap; }
  th { color: ${css.grey2}; font-weight: 600; }
  th.num, td.num { text-align: right; }
  tbody tr { cursor: pointer; }
  tbody tr:hover { background: ${css.grey8}; }
  .name { font-weight: 600; color: ${css.primaryColor}; }
  .muted { color: ${css.grey4}; }
`;

export const ResigenModalStyled = styled(ModalStyled)`
  .modal-window { width: 34rem; max-width: 100%; }
  .meta-row { color: ${css.grey3}; font-size: 0.9rem; margin: 0.6rem 0 0.15rem; }
  /* The Input wrapper span (and its inner <input>) default to browser width — stretch both. */
  .input { display: block; width: 100%; }
  .input input { width: 100%; box-sizing: border-box; }
  .adders { display: flex; gap: 1.5rem; margin: 0.75rem 0; }
  .adders label { display: flex; gap: 0.4rem; align-items: center; cursor: pointer; }
  .preview { border: ${css.inputBorder}; border-radius: 0.5rem; padding: 0.6rem 0.9rem; margin-top: 0.9rem; background: ${css.grey8}; }
  .preview .k { color: ${css.grey4}; font-size: 0.8rem; }
  .preview .v { font-size: 1.3rem; font-weight: bold; }
  .preview .err { color: ${css.red1}; font-size: 0.85rem; }
  .roof-estimate { margin-top: 0.4rem; font-size: 0.85rem; color: ${css.grey3}; display: flex; align-items: baseline; gap: 0.5rem; }
  .roof-estimate .existing { color: ${css.orange}; font-weight: 600; }
  .roof-estimate .capped { color: ${css.grey2}; font-weight: 600; }
  .roof-estimate.muted { color: ${css.grey5}; }
  .roof-estimate .link-btn { border: none; background: none; padding: 0; font: inherit; color: ${css.primaryColor}; cursor: pointer; }
  .modal-buttons { display: flex; gap: 1rem; justify-content: flex-end; margin-top: 1rem; }
`;

/**
 * Intake / edit modal: the four dealer inputs with a LIVE prepayment preview computed client-side
 * from the org's effective tables (fetched once). Save persists the server-computed snapshot.
 */
export const ResigenProjectModal = (props: { existing?: ResigenProject; onSaved?: (id: string) => void; onClose: () => void }) => {
  const { existing, onSaved, onClose } = props;
  const [tables, setTables] = useState<ResigenTables>();
  const [name, setName] = useState(existing?.name || '');
  const [systemSizeKw, setSystemSizeKw] = useState(existing?.systemSizeKw != null ? String(existing.systemSizeKw) : '');
  const [essCapacityKwh, setEssCapacityKwh] = useState(existing?.essCapacityKwh != null ? String(existing.essCapacityKwh) : '');
  const [energyCommunity, setEnergyCommunity] = useState(!!existing?.energyCommunity);
  const [disadvantagedCommunity, setDisadvantagedCommunity] = useState(!!existing?.disadvantagedCommunity);
  const [address, setAddress] = useState<{ formatted?: string, lat?: number, lng?: number }>({ formatted: existing?.address, lat: existing?.lat, lng: existing?.lng });
  const [roof, setRoof] = useState<{ maxKwDc?: number, maxPanels?: number, existingSolar?: boolean } | null>();
  useEffect(() => { resigenGetTables.call().then(setTables).catch(() => undefined); }, []);
  // Address picked → ask the Solar API for the roof's max system size (suggestion only; user can ignore).
  useEffect(() => {
    if (address.lat == null || address.lng == null) {
      setRoof(undefined);
      return;
    }
    let stale = false;
    resigenRoofEstimate.call({ lat: address.lat, lng: address.lng }).then((r) => { if (!stale) setRoof(r); }).catch(() => undefined);
    return () => { stale = true; };
  }, [address.lat, address.lng]);

  // Largest sellable system = the top solar tier's ceiling (50 kW on the Luminia defaults). Read from
  // the tables so an org with different tiers isn't capped at someone else's limit.
  const maxTierKw = tables ? Math.max(...tables.solarTiers.map((t) => t.maxKw)) : undefined;

  let preview: { prepaymentAmount?: number, error?: string } = {};
  if (tables && (systemSizeKw !== '' || essCapacityKwh !== '')) {
    try {
      preview = { prepaymentAmount: resigenCalculate(tables, { systemSizeKw: Number(systemSizeKw) || 0, essCapacityKwh: Number(essCapacityKwh) || 0, energyCommunity, disadvantagedCommunity }).prepaymentAmount };
    }
    catch (error: any) {
      preview = { error: `${error?.message || error}` };
    }
  }

  const save = toastOnError(async () => {
    const id = await resigenSaveProject.call({
      _id: existing?._id, name,
      address: address.formatted || undefined, lat: address.lat, lng: address.lng,
      systemSizeKw: Number(systemSizeKw) || 0, essCapacityKwh: Number(essCapacityKwh) || 0,
      energyCommunity, disadvantagedCommunity,
    });
    onClose();
    onSaved?.(id);
  });

  return (
    <Modal styled={ResigenModalStyled} title={existing ? existing.name : strings.NewResiGenProject()} onClose={onClose}>
      <div className="meta-row">{strings.Name()}</div>
      <Input value={name} onChange={(v) => setName(v || '')} />
      <div className="meta-row">{strings.Address()} ({strings.Optional()})</div>
      <AddressInput inline placeholder={address.formatted} onChange={(a) => setAddress({ formatted: a?.formatted, lat: a?.location?.lat, lng: a?.location?.lng })} />
      {
        roof?.maxKwDc != null &&
        (() => {
          // Whole commercial rooftops routinely exceed the residential tiers, so the estimate is shown
          // capped at the top tier (both sizes visible) and that capped figure is what "Use" fills.
          const capped = maxTierKw != null && roof.maxKwDc > maxTierKw ? maxTierKw : undefined;
          const cappedPanels = capped != null && roof.maxPanels ? Math.round((capped * roof.maxPanels) / roof.maxKwDc) : undefined;
          return (
            <div className="roof-estimate">
              {strings.RoofEstimate()}: {roof.maxKwDc}{capped != null && <> → <span className="capped">{capped}</span></>} kW
              {' '}({roof.maxPanels}{cappedPanels != null && <> → {cappedPanels}</>} {strings.Panels()})
              {roof.existingSolar && <span className="existing"> · {strings.ExistingSolarDetected()}</span>}
              <button className="link-btn" onClick={() => setSystemSizeKw(String(capped ?? roof.maxKwDc))}>{strings.Use()}</button>
            </div>
          );
        })()
      }
      {roof === null && address.lat != null && <div className="roof-estimate muted">{strings.NoRoofData()}</div>}
      <div className="meta-row">{strings.SolarSystemSizeKwDc()}</div>
      <Input type="number" value={systemSizeKw} onChange={(v) => setSystemSizeKw(v || '')} />
      <div className="meta-row">{strings.EssCapacityKwh()}</div>
      <Input type="number" value={essCapacityKwh} onChange={(v) => setEssCapacityKwh(v || '')} />
      <div className="adders">
        <label><input type="checkbox" checked={energyCommunity} onChange={(e) => setEnergyCommunity(e.target.checked)} />{strings.EnergyCommunityAdder()}</label>
        <label><input type="checkbox" checked={disadvantagedCommunity} onChange={(e) => setDisadvantagedCommunity(e.target.checked)} />{strings.DisadvantagedCommunityAdder()}</label>
      </div>
      <div className="preview">
        <div className="k">{strings.PrepaymentAmount()}</div>
        {preview.error ? <div className="err">{preview.error}</div> : <div className="v">{preview.prepaymentAmount != null ? resigenMoney(preview.prepaymentAmount) : '—'}</div>}
      </div>
      <div className="modal-buttons">
        <Button className="secondary" onClick={onClose}>{strings.Cancel()}</Button>
        <Button className="primary" onClick={() => save()} disabled={!name.trim() || !!preview.error || preview.prepaymentAmount == null}>{existing ? strings.Save() : strings.Create()}</Button>
      </div>
    </Modal>
  );
};

/** Whether this user may use ResiGen (mirrors the server guard). */
export const resigenCanAccess = (user?: Meteor.User | null, account?: { roles?: string[] }) =>
  !!user?.isSuperUser || !!account?.roles?.includes(Roles.resigen);

/** ResiGen workspace: the org's prepayment projects with search + intake. */
export const ResiGenPage = () => {
  const navigate = useNavigate();
  const user = useUser();
  const account = useFindOne(() => Accounts.find({ orgId: user?.orgId, userId: user?._id }), [user?._id]);
  const loading = useSubscribe(Publications.resigenProjects);
  const projects = useTracker(() => ResigenProjects.find({}, { sort: { createdAt: -1 } }).fetch(), []);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  // Held as the fetched tables (not a boolean): the editor seeds its state once, on mount.
  const [orgTables, setOrgTables] = useState<ResigenTables>();
  const openOrgTables = toastOnError(async () => setOrgTables(await resigenGetTables.call()));
  if (!resigenCanAccess(user, account)) return <NotPermittedPage />;
  const query = search.trim().toLowerCase();
  const rows = query ? projects.filter((p) => p.name.toLowerCase().includes(query)) : projects;
  return (
    <ResiGenPageStyled>
      <PageHeading
        left={<h1>{strings.ResiGen()}</h1>}
        right={
          <div className="heading-buttons">
            {user?.isSuperUser && <Button className="secondary" onClick={() => openOrgTables()}>{strings.ValuationTables()}</Button>}
            <Button className="primary" onClick={() => setShowCreate(true)}>{strings.New()}</Button>
          </div>
        }
      />
      <PageBody>
        <div className="controls">
          <div className="search"><Input value={search} onChange={(v) => setSearch(v || '')} placeholder={strings.Search()} /></div>
        </div>
        {
          loading() ?
            <LoadingFlex />
            :
            <table>
              <thead>
                <tr>
                  <th>{strings.Name()}</th>
                  <th className="num">{strings.SolarSystemSizeKwDc()}</th>
                  <th className="num">{strings.EssCapacityKwh()}</th>
                  <th>{strings.ItcStatus()}</th>
                  <th className="num">{strings.PrepaymentAmount()}</th>
                  <th>{strings.Created()}</th>
                </tr>
              </thead>
              <tbody>
                {
                  rows.map((p) => (
                    <tr key={p._id} onClick={() => navigate(`/resigen/${p._id}/view`)}>
                      <td className="name">{p.name}</td>
                      <td className="num">{formatNumber(p.systemSizeKw, 2)}</td>
                      <td className="num">{formatNumber(p.essCapacityKwh, 2)}</td>
                      <td>{resigenItcStatusLabels[p.calc?.itcStatus] || '—'}</td>
                      <td className="num">{resigenMoney(p.calc?.prepaymentAmount)}</td>
                      <td>{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))
                }
                {!rows.length && <tr><td colSpan={6} className="muted">—</td></tr>}
              </tbody>
            </table>
        }
      </PageBody>
      {showCreate && <ResigenProjectModal onClose={() => setShowCreate(false)} onSaved={(id) => { toast.success(strings.Done()); navigate(`/resigen/${id}/view`); }} />}
      {orgTables && user?.orgId && <ResigenTablesModal orgId={user.orgId} current={orgTables} onClose={() => setOrgTables(undefined)} />}
    </ResiGenPageStyled>
  );
};
